import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import Layout from './Layout';
import PageHeader from './PageHeader';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Snackbar,
  Alert,
  Stack,
  TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { paySaleDue, returnSaleItem, refundSale, fetchSaleDetails } from '../api';

const SalesDetailsPage = () => {
  const { saleId } = useParams();
  const [sale, setSale] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [returnQuantity, setReturnQuantity] = useState(0);
  const [returnReason, setReturnReason] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showDuePaymentModal, setShowDuePaymentModal] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const toggleDuePaymentModal = () => setShowDuePaymentModal(!showDuePaymentModal);
  const toggleReturnItemModal = () => setShowReturnModal(!showReturnModal);

  useEffect(() => {
    fetchSale();
    // eslint-disable-next-line
  }, [saleId]);

  const fetchSale = () => {
    setLoading(true);
    fetchSaleDetails(saleId)
      .then((data) => {
        setSale(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching sale details:', error);
        setLoading(false);
      });
  };

  const handlePayment = (e) => {
    e.preventDefault();
    if (paymentAmount <= 0 || paymentAmount > sale.due_amount) {
      setMessage('Invalid payment amount.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    paySaleDue(saleId, { payment_amount: paymentAmount })
      .then(() => {
        setMessage('Payment successful!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchSale();
        setPaymentAmount(0);
        setShowDuePaymentModal(false);
      })
      .catch((error) => {
        setMessage('Failed to process payment.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        console.error('Error processing payment:', error);
      });
  };

  const handleReturnClick = (item) => {
    setSelectedItem(item);
    setReturnQuantity(0);
    setShowReturnModal(true);
  };

  const handleReturnSubmit = (e) => {
    e.preventDefault();
    if (returnQuantity <= 0 || returnQuantity > selectedItem.quantity) {
      setMessage('Invalid return quantity.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    returnSaleItem(saleId, {
      item_id: selectedItem.id,
      return_quantity: returnQuantity,
      return_reason: returnReason
    })
      .then(() => {
        setMessage('Item returned successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setShowReturnModal(false);
        fetchSale();
      })
      .catch((error) => {
        setMessage('Failed to return item.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        console.error('Error returning item:', error);
      });
  };

  const handleRefund = async (saleId) => {
    try {
      await refundSale(saleId);
      setMessage('Refund Successful');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchSale();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Refund failed');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      console.error('Refund Error:', error);
    }
  };

  if (loading) {
    return <Box p={4}><Typography>Loading...</Typography></Box>;
  }
  if (!sale) {
    return <Box p={4}><Typography>Sale not found.</Typography></Box>;
  }

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <PageHeader title="Sale Details" subtitle="Detailed view of the selected sale." />
        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
              <Box flex={1}>
                <Typography variant="h6" gutterBottom>Sale ID: {sale.id}</Typography>
                <Paper variant="outlined" sx={{ p: 2, mb: 2, background: '#f8f9fa', borderLeft: '4px solid #1976d2' }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight="bold">Customer Information</Typography>
                    <Typography variant="body1">👤 <b>{sale.customer_name}</b></Typography>
                    <Typography variant="body2" color="text.secondary">Date: {sale.date}</Typography>
                  </Stack>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, mb: 2, background: '#f5f7fa', borderLeft: '4px solid #43a047' }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight="bold">Sale Summary</Typography>
                    <Typography variant="body1">💵 Total Amount: <b>{sale.total_amount}</b></Typography>
                    <Typography variant="body1">💸 Paid Amount: <b>{sale.paid_amount}</b></Typography>
                    <Typography variant="body1">🏷️ Overall Discount: <b>{sale.overall_discount}</b></Typography>
                    <Typography variant="body1">
                      🧾 Due Amount: <span style={{ color: sale.due_amount > 0 ? 'red' : 'green', fontWeight: 'bold' }}>{sale.due_amount}</span>
                      {sale.due_amount < 0 && (
                        <Button
                          size="small"
                          color="warning"
                          sx={{ ml: 2 }}
                          onClick={() => handleRefund(sale.id)}
                        >
                          Refund cash
                        </Button>
                      )}
                      {sale.due_amount > 0 && (
                        <Button
                          size="small"
                          sx={{ ml: 2, backgroundColor: '#4caf50', color: '#fff', fontWeight: 'bold', borderRadius: 2, boxShadow: 1, '&:hover': { backgroundColor: '#388e3c', color: '#fff', boxShadow: 2 } }}
                          onClick={toggleDuePaymentModal}
                        >
                          Pay due amount
                        </Button>
                      )}
                    </Typography>
                  </Stack>
                </Paper>
              </Box>
            </Stack>
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Items</Typography>
            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 1, background: '#f8f9fa' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ background: '#e3f2fd' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Size</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Color</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Quantity</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Returned</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Price</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Discount</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sale.saleitems.map((item, index) => (
                    <TableRow key={index} hover sx={{ background: index % 2 === 0 ? '#ffffff' : '#f4f6f8' }}>
                      <TableCell>
                        <Typography fontWeight="bold">{item.product_name}</Typography>
                      </TableCell>
                      <TableCell>{item.size || '-'}</TableCell>
                      <TableCell>{item.color || '-'}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{item.return_quantity}</TableCell>
                      <TableCell>৳{item.price_per_unit}</TableCell>
                      <TableCell>{item.discount > 0 ? `৳${item.discount}` : '-'}</TableCell>
                      <TableCell>
                        <Typography fontWeight="bold" color={item.discount > 0 ? 'primary' : 'text.primary'}>
                          ৳{(item.price_per_unit - item.discount) * item.quantity}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {item.quantity > 0 && (
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{
                              fontWeight: 'bold',
                              borderRadius: 2,
                              color: '#1976d2',
                              borderColor: '#1976d2',
                              boxShadow: 1,
                              transition: 'all 0.2s',
                              '&:hover': {
                                backgroundColor: '#bbdefb', // softer blue shade
                                color: '#0d47a1',
                                borderColor: '#1565c0',
                                boxShadow: 2,
                              },
                            }}
                            onClick={() => handleReturnClick(item)}
                          >
                            Return
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
        {/* Return Modal */}
        <Dialog open={showReturnModal} onClose={toggleReturnItemModal}>
          <DialogTitle sx={{ pr: 6 }}>
            Return Item: {selectedItem?.product_name}
            <IconButton
              aria-label="close"
              onClick={toggleReturnItemModal}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography>Available to return: {selectedItem?.quantity}</Typography>
            <Box component="form" onSubmit={handleReturnSubmit} sx={{ mt: 2 }}>
              <TextField
                type="number"
                label="Return Quantity"
                value={returnQuantity}
                onChange={(e) => setReturnQuantity(Number(e.target.value))}
                inputProps={{ min: 1, max: selectedItem?.quantity }}
                required
                fullWidth
                sx={{ mb: 2 }}
              />
              <TextField
                label="Return Reason (optional)"
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
              />
              <DialogActions>
                <Button onClick={toggleReturnItemModal}>Cancel</Button>
                <Button type="submit" variant="contained">Confirm Return</Button>
              </DialogActions>
            </Box>
          </DialogContent>
        </Dialog>
        {/* Due Payment Modal */}
        <Dialog open={showDuePaymentModal} onClose={toggleDuePaymentModal}>
          <DialogTitle sx={{ pr: 6 }}>
            Pay Due Amount (৳{Math.round(sale.due_amount)})
            <IconButton
              aria-label="close"
              onClick={toggleDuePaymentModal}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box component="form" onSubmit={handlePayment} sx={{ mt: 2 }}>
              <TextField
                type="number"
                label="Payment Amount"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(Number(e.target.value))}
                inputProps={{ min: 1, max: sale.due_amount }}
                required
                fullWidth
                sx={{ mb: 2 }}
              />
              <DialogActions>
                <Button onClick={toggleDuePaymentModal}>Cancel</Button>
                <Button type="submit" variant="contained" disabled={sale.due_amount <= 0}>Pay Now</Button>
              </DialogActions>
            </Box>
          </DialogContent>
        </Dialog>
        <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)}>
          <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default SalesDetailsPage;
